import { DIRECTORY_TABS } from '@components/common/constants';
import SourceFactory from '@components/sources/sourceFactory';
import { SourceDefinitionCategory } from '@stores/types';
import { ApiPermission, PermissionActionType } from '@api';
import {
  ResourceType,
  NewPermissionModelQuery,
  ActionType,
  BaseResourceType,
  RESOURCE_GROUPS,
} from '../types';
import { actionTypeToMessage, resourceTypeToMessage } from '../constants';
import { SourceCategory } from '@stores/source/types';

/**
 * Checks if a user has a specific permission for a resource.
 *
 * @param accessPolicy - The user's access policy containing all permissions
 * @param query - The permission query specifying action type, resource type, and optional resource ID
 * @returns true if the user has the specified permission, false otherwise
 */
const hasPermissionForResource = (
  accessPolicy: ApiPermission[],
  query: {
    actionType: PermissionActionType;
    resourceType: BaseResourceType;
    resourceId?: string;
  },
): boolean => {
  const permissionForResource = accessPolicy.find(
    (permission) => permission.resourceType === query.resourceType,
  );
  const permissionAction = permissionForResource?.actions.find(
    (action) => action.name === query.actionType,
  );

  if (!permissionAction) {
    return false;
  }

  if (!permissionAction.resourceIds || permissionAction.resourceIds === '*') {
    return true;
  }

  return !!query.resourceId && permissionAction.resourceIds.includes(query.resourceId);
};

/**
 * Verifies that a user can perform connection operations on a specific resource.
 * Connection operations require both CONNECT and EDIT permissions.
 *
 * @param accessPolicy - The user's access policy
 * @param resource - The resource to check permissions for
 * @returns true if user can perform connect and edit operations on resource, false otherwise
 */
const canPerformConnectOperationOnResource = (
  accessPolicy: ApiPermission[],
  query: {
    resourceType: BaseResourceType;
    resourceId: string;
  },
) =>
  hasPermissionForResource(accessPolicy, {
    actionType: ActionType.CONNECT,
    resourceType: query.resourceType,
    resourceId: query.resourceId,
  }) &&
  hasPermissionForResource(accessPolicy, {
    actionType: ActionType.EDIT,
    resourceType: query.resourceType,
    resourceId: query.resourceId,
  });

/**
 * Verifies that a user can create new resources of a type and connect to them.
 * This requires both CREATE_DELETE and CONNECT permissions.
 *
 * @param accessPolicy - The user's access policy
 * @param resourceType - The type of resource to check
 * @returns true if user can create and connect a resource of this type, false otherwise
 */
const canPerformCreateAndConnectOperationOnResource = (
  accessPolicy: ApiPermission[],
  query: {
    resourceType: BaseResourceType;
  },
) =>
  hasPermissionForResource(accessPolicy, {
    actionType: ActionType.CREATE_DELETE,
    resourceType: query.resourceType,
  }) &&
  hasPermissionForResource(accessPolicy, {
    actionType: ActionType.CONNECT,
    resourceType: query.resourceType,
  });

/**
 * Handles permission checking for abstract resource types (SOURCE, RETL) by
 * checking permissions against their concrete resource type groups.
 *
 * For concrete resource types, it directly applies the callback.
 * For abstract resource types (SOURCE/RETL), it checks if ANY of the
 * concrete types in the group satisfy the permission check.
 *
 * @param resourceType - The resource type to check (can be abstract or concrete)
 * @param permissionChecker - Function that checks permissions for a concrete resource type
 * @returns true if permissions are satisfied for the resource type, false otherwise
 */
const checkPermissionForResourceTypeGroup = (
  resourceType: ResourceType,
  permissionChecker: (resourceType: BaseResourceType) => boolean,
) => {
  if (resourceType === ResourceType.SOURCE || resourceType === ResourceType.RETL) {
    const concreteResourceTypes =
      resourceType === ResourceType.SOURCE ? RESOURCE_GROUPS.SOURCE : RESOURCE_GROUPS.RETL;

    return concreteResourceTypes.some(permissionChecker);
  }

  return permissionChecker(resourceType);
};

/**
 * Main permission checking function that evaluates whether a user can perform
 * a specific action based on their access policy.
 *
 * @param accessPolicy - The user's complete access policy
 * @param query - The permission query containing the action and resource details
 * @returns true if the action is allowed, false otherwise
 */
export const checkPermission = (
  accessPolicy: ApiPermission[],
  query: NewPermissionModelQuery,
): boolean => {
  switch (query.actionType) {
    case ActionType.CONNECT:
    case ActionType.DISCONNECT: {
      // For connecting two specific resources, both must be connectable
      const canConnectToOriginResource = canPerformConnectOperationOnResource(accessPolicy, {
        resourceType: query.resources[0].type,
        resourceId: query.resources[0].id,
      });

      const canConnectAnyTargetResource = canPerformConnectOperationOnResource(accessPolicy, {
        resourceType: query.resources[1].type,
        resourceId: query.resources[1].id,
      });

      return canConnectToOriginResource && canConnectAnyTargetResource;
    }

    case ActionType.CONNECT_EXISTING: {
      // Check if origin resource is connectable
      const canConnectToOriginResource = canPerformConnectOperationOnResource(accessPolicy, {
        resourceType: query.resources[0].type,
        resourceId: query.resources[0].id,
      });

      if (!canConnectToOriginResource) {
        return false;
      }

      // Check if at least one target resource is connectable
      const canCreateAndConnectAnyTargetType = checkPermissionForResourceTypeGroup(
        query.resources[1].type,
        (concreteResourceType) =>
          query.resources[1].id.some((targetId) =>
            canPerformConnectOperationOnResource(accessPolicy, {
              resourceType: concreteResourceType,
              resourceId: targetId,
            }),
          ),
      );

      return canCreateAndConnectAnyTargetType;
    }

    case ActionType.CONNECT_NEW: {
      // Check if origin resource is connectable
      const canConnectToOriginResource = canPerformConnectOperationOnResource(accessPolicy, {
        resourceType: query.resources[0].type,
        resourceId: query.resources[0].id,
      });

      if (!canConnectToOriginResource) {
        return false;
      }

      // Check if user can create and connect the target resource type
      const resourceTypes = Array.isArray(query.resources[1].type)
        ? query.resources[1].type
        : [query.resources[1].type];

      const canCreateAndConnectToTarget = resourceTypes.some((resourceType) =>
        checkPermissionForResourceTypeGroup(resourceType, (concreteResourceType) =>
          canPerformCreateAndConnectOperationOnResource(accessPolicy, {
            resourceType: concreteResourceType,
          }),
        ),
      );

      return canCreateAndConnectToTarget;
    }

    case ActionType.CREATE:
      return checkPermissionForResourceTypeGroup(query.resource.type, (resourceType) =>
        hasPermissionForResource(accessPolicy, {
          actionType: ActionType.CREATE_DELETE,
          resourceType,
        }),
      );

    case ActionType.DELETE:
      return hasPermissionForResource(accessPolicy, {
        actionType: ActionType.CREATE_DELETE,
        resourceType: query.resource.type,
        resourceId: getResourceId(query.resource),
      });

    case ActionType.EDIT:
    case ActionType.PII_FAILURE_SAMPLES:
    case ActionType.PII_LIVE_EVENTS:
      return hasPermissionForResource(accessPolicy, {
        actionType: query.actionType,
        resourceType: query.resource.type,
        resourceId: getResourceId(query.resource),
      });

    default:
      return false;
  }
};

const getResourceActionPhrase = (
  actionMessage: string,
  resourceType: ResourceType,
  resourceId?: string,
) => {
  const resourceMessageData = resourceTypeToMessage[resourceType];
  const resourceMessage = resourceId ? resourceMessageData.singular : resourceMessageData.plural;

  return resourceMessageData.includeAction && actionMessage
    ? `${actionMessage} ${resourceMessage}`
    : resourceMessage;
};

const getResourceId = (resource: object) =>
  'id' in resource && typeof resource.id === 'string' ? resource.id : undefined;

export const constructReasonForNoPermission = (query: NewPermissionModelQuery): string => {
  const actionMessage = actionTypeToMessage[query.actionType];

  if (
    query.actionType === ActionType.CONNECT ||
    query.actionType === ActionType.CONNECT_EXISTING ||
    query.actionType === ActionType.CONNECT_NEW ||
    query.actionType === ActionType.DISCONNECT
  ) {
    const originResourceActionPhrase = getResourceActionPhrase(
      '',
      query.resources[0].type,
      query.resources[0].id,
    );

    const targetResourceType = Array.isArray(query.resources[1].type)
      ? ResourceType.SOURCE
      : query.resources[1].type;

    const targetResourceActionPhrase = getResourceActionPhrase(
      actionMessage,
      targetResourceType,
      getResourceId(query.resources[1]),
    );

    const connectivityPreposition = query.actionType === ActionType.DISCONNECT ? 'from' : 'to';

    return `You do not have permission to ${targetResourceActionPhrase} ${connectivityPreposition} ${originResourceActionPhrase}.`;
  }

  return `You do not have permission to ${getResourceActionPhrase(
    actionMessage,
    query.resource.type,
    getResourceId(query.resource),
  )}.`;
};

export const constructPermissionResult = (isAllowed: boolean, denialReason: string) => ({
  isAllowed,
  ...(!isAllowed && { denialReason }),
});

const isEventStream = (sourceDefinitionCategory: SourceDefinitionCategory) =>
  SourceFactory(sourceDefinitionCategory).isPartOf(DIRECTORY_TABS.EVENT_STREAMS);

const isRETL = (sourceDefinitionCategory: SourceDefinitionCategory) =>
  SourceFactory(sourceDefinitionCategory).isPartOf(DIRECTORY_TABS.WAREHOUSE_ACTIONS);

export const getResourceTypeFromSourceDefinitionCategory = (
  sourceDefinitionCategory: SourceDefinitionCategory,
) => {
  if (isEventStream(sourceDefinitionCategory)) {
    return ResourceType.EVENT_STREAM;
  }

  if (isRETL(sourceDefinitionCategory)) {
    return ResourceType.RETL;
  }

  return ResourceType.EXTRACT;
};

// ts-prune-ignore-next
export const getResourceTypeFromSourceCategory = (sourceCategory: SourceCategory) => {
  if (sourceCategory.category === 'event') {
    return ResourceType.EVENT_STREAM;
  }
  if (sourceCategory.category === 'extract') {
    return ResourceType.EXTRACT;
  }
  if (sourceCategory.subcategory === 'table') {
    return ResourceType.TABLE;
  }
  if (sourceCategory.subcategory === 'model') {
    return ResourceType.SQL_MODEL;
  }
  return ResourceType.AUDIENCE;
};
